import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import UserMenu from "../components/UserMenu";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { FolderOpen, Factory, LogOut, ArrowRight, User, ShieldCheck } from "lucide-react";
import { isCustomerPro, CUSTOMER_PRO_LANDING } from "../lib/roles";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ModuleSelector({ user, isAdmin = false, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [moduleAccess, setModuleAccess] = useState({
    has_project_module: false,
    has_safety_module: false,
    default_module: null
  });
  const [rememberChoice, setRememberChoice] = useState(false);

  useEffect(() => {
    checkModuleAccess();
  }, []);

  const checkModuleAccess = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/modules/access`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const access = response.data;
      setModuleAccess(access);

      // CUSTOMER_PRO users have exactly one destination — the visual Container Planner —
      // so we bypass the module chooser entirely, no matter what preference they hold.
      if (isCustomerPro(user)) {
        navigate(CUSTOMER_PRO_LANDING, { replace: true });
        return;
      }

      // Check if user explicitly navigated to selector (via Switch Module button)
      const forceShowSelector = sessionStorage.getItem("forceModuleSelector");
      if (forceShowSelector) {
        sessionStorage.removeItem("forceModuleSelector");
        setLoading(false);
        return;
      }

      // Auto-redirect based on saved preference
      if (access.default_module) {
        // User has a preference set
        if (access.default_module === "project" && access.has_project_module) {
          navigate("/", { replace: true });
          return;
        } else if (access.default_module === "safety" && access.has_safety_module) {
          navigate("/safety", { replace: true });
          return;
        }
      }
      
      // If only one module access, redirect directly
      if (access.has_project_module && !access.has_safety_module) {
        navigate("/", { replace: true });
        return;
      }
      if (access.has_safety_module && !access.has_project_module) {
        navigate("/safety", { replace: true });
        return;
      }
      
      // Both modules and no preference - show selector
      setLoading(false);
    } catch (error) {
      console.error("Failed to check module access:", error);
      setLoading(false);
    }
  };

  const handleModuleSelect = async (module) => {
    if (rememberChoice) {
      try {
        const token = localStorage.getItem("token");
        await axios.put(`${API}/modules/preference`, 
          { default_module: module },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Preference saved");
      } catch (error) {
        console.error("Failed to save preference:", error);
      }
    }
    
    if (module === "project") {
      navigate("/");
    } else {
      navigate("/safety");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-white mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  // No module access
  if (!moduleAccess.has_project_module && !moduleAccess.has_safety_module) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-6">
            <h2 className="text-xl font-bold text-red-400 mb-2">No Module Access</h2>
            <p className="text-gray-300 mb-4">
              You don't have access to any modules. Please contact your administrator.
            </p>
            <Button onClick={onLogout} variant="outline" className="border-red-500/30 text-red-400">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800" data-testid="module-selector">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="absolute top-4 right-4 flex items-center gap-3">
        <UserMenu user={user} onLogout={onLogout} variant="header" />
      </div>

      {/* Main Content */}
      <div className="flex flex-col items-center justify-center min-h-screen px-4">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-white mb-2">
            Welcome back, {user?.name || user?.email?.split('@')[0]}! 👋
          </h1>
          <p className="text-white/70">Select a module to continue</p>
        </div>

        <div className={`grid grid-cols-1 ${isAdmin ? "md:grid-cols-3" : "md:grid-cols-2"} gap-6 max-w-4xl w-full`}>
          {/* Project Management Card */}
          {moduleAccess.has_project_module && (
            <div 
              onClick={() => handleModuleSelect("project")}
              className="group bg-white/5 hover:bg-white/10 border border-white/10 hover:border-blue-500/50 rounded-xl p-8 cursor-pointer transition-all duration-300 hover:scale-105"
              data-testid="project-module-card"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-500/30 transition-colors">
                  <FolderOpen className="w-8 h-8 text-blue-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Project Management</h2>
                <p className="text-white/60 text-sm mb-6">
                  Documents, Approvals, Emails, Workflows
                </p>
                <div className="flex items-center justify-center gap-2 text-blue-400 font-medium">
                  Enter <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          )}

          {/* Safety Equipment Card */}
          {moduleAccess.has_safety_module && (
            <div 
              onClick={() => handleModuleSelect("safety")}
              className="group bg-white/5 hover:bg-white/10 border border-white/10 hover:border-emerald-500/50 rounded-xl p-8 cursor-pointer transition-all duration-300 hover:scale-105"
              data-testid="safety-module-card"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-emerald-500/30 transition-colors">
                  <Factory className="w-8 h-8 text-emerald-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Safety Equipment</h2>
                <p className="text-white/60 text-sm mb-6">
                  PO Management, QR Code Generation
                </p>
                <div className="flex items-center justify-center gap-2 text-emerald-400 font-medium">
                  Enter <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          )}

          {/* Administration Card (admins only) */}
          {isAdmin && (
            <div 
              onClick={() => navigate("/role-management")}
              className="group bg-white/5 hover:bg-white/10 border border-white/10 hover:border-amber-500/50 rounded-xl p-8 cursor-pointer transition-all duration-300 hover:scale-105"
              data-testid="admin-module-card"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-amber-500/30 transition-colors">
                  <ShieldCheck className="w-8 h-8 text-amber-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Administration</h2>
                <p className="text-white/60 text-sm mb-6">
                  Roles, Permissions & User Management
                </p>
                <div className="flex items-center justify-center gap-2 text-amber-400 font-medium">
                  Enter <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Remember Choice */}
        <div className="mt-8 flex items-center gap-2">
          <Checkbox 
            id="remember" 
            checked={rememberChoice} 
            onCheckedChange={setRememberChoice}
            className="border-white/30"
            data-testid="remember-choice-checkbox"
          />
          <Label htmlFor="remember" className="text-white/70 cursor-pointer">
            Remember my choice (go directly next time)
          </Label>
        </div>
      </div>
    </div>
  );
}

export default ModuleSelector;
